import { DateTime } from '@ksyos/date-time';
import { Button } from '@ksyos/design-system';
import { formatString } from '@common/utils/formatString';
import type { ValidationErrors } from '../../utils/validation';
import type { IDiagnosticReportGetOne } from '../interfaces/diagnosticReportGetOne';
import type { IRequestGetOne } from '../interfaces/requestGetOne';
import { getDisplayName } from '../../../common/utils/getDisplayName';
import { requestEditRecipientSchema } from '../../../common/validation/request';
import { fetchJson } from '../../store/fetch';
import { getSelectedLanguage } from '../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../utils/validation';
import { Dropdown, ReadOnlyInputText } from './common';
import { Component } from './Component';
import { FormGroup } from './forms/FormGroup';
import { FormLabel } from './forms/FormLabel';
import { Modal } from './modals/Modal';

interface IAssignToColleagueProps {
    diagnosticReport?: IDiagnosticReportGetOne;
    onAssign: () => Promise<void>;
    onClose: () => Promise<void>;
    request: IRequestGetOne;
}

interface IAssignToColleagueState {
    errors: ValidationErrors;
    recipientPractitionerId: string | null;
    serviceProviders: IServiceProvider[];
}

interface IServiceProvider {
    absenceEndDate: string | null;
    id: string;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

export class AssignToColleagueModal extends Component<IAssignToColleagueProps, IAssignToColleagueState> {
    constructor(props: IAssignToColleagueProps, context?: Record<string, never>) {
        super(props, context);
        this.state = {
            errors: emptyValidationErrors,
            recipientPractitionerId: null,
            serviceProviders: [],
        };
    }

    public async componentDidMount() {
        await this.load();
    }

    public render() {
        const translate = getSelectedLanguage();

        return (
            <Modal
                actionButtons={[
                    <Button
                        key="close"
                        onClick={async () => await this.props.onClose()}
                        variant="outline"
                    >
                        {translate.common.cancel}
                    </Button>,
                    <Button
                        isDisabled={this.state.recipientPractitionerId === null}
                        key="assign"
                        onClick={async () => await this.assignToColleague()}
                        variant="solid"
                    >
                        {translate.common.assign}
                    </Button>,
                ]}
                isOpen
                onClose={async () => await this.props.onClose()}
                size="medium"
                title={translate.diagnosticReport.assignConsultation}
            >
                {this.state.serviceProviders && (
                    <FormGroup
                        className="mb-0"
                        hasErrors={this.state.errors.has('recipientPractitionerId')}
                    >
                        <FormLabel stacked>{translate.diagnosticReport.assignColleague}</FormLabel>
                        {this.showColleagueOrDropDown()}
                    </FormGroup>
                )}
            </Modal>
        );
    }
    private showColleagueOrDropDown() {
        if (this.state.serviceProviders.length > 1) {
            return (
                <Dropdown
                    error={this.state.errors.get('recipientPractitionerId')}
                    onChange={async (value) => await this.setState({ recipientPractitionerId: value })}
                    onDoValidate={async () => await this.validateOne()}
                    selectOptions={this.getServiceProviderNameSelectOptions()}
                    value={this.state.recipientPractitionerId}
                />
            );
        } else if (this.state.serviceProviders.length === 1) {
            return <ReadOnlyInputText value={this.createPractitionerLabel(this.state.serviceProviders[0])} />;
        }
    }
    private async validateOne() {
        await this.setState(({ errors }) => ({
            errors: updateValidationErrorsForOne(
                requestEditRecipientSchema,
                this.state,
                errors,
                'recipientPractitionerId'
            ),
        }));
    }

    private getServiceProviderNameSelectOptions() {
        const translate = getSelectedLanguage();
        const options = [{ text: formatString(translate.common.selectItem, [translate.common.colleague]), value: '' }];

        return options.concat(
            this.state.serviceProviders.map((item) => {
                return { text: this.createPractitionerLabel(item), value: item.id };
            })
        );
    }

    private createPractitionerLabel(practitioner: IServiceProvider) {
        const translate = getSelectedLanguage();
        let text = getDisplayName(practitioner);
        if (practitioner.absenceEndDate) {
            text += ` (${translate.request.absentUpToAndIncluding} ${DateTime.from(practitioner.absenceEndDate).format(
                'DD-MM-YYYY'
            )})`;
        }

        return text;
    }

    private async load() {
        const result = await fetchJson(
            `/api/serviceProvidersList/${this.props.request.serviceId}/practitioners?organizationId=${this.props.request.recipientOrganizationId}&onlyColleagues=true`,
            'GET'
        );
        await this.setState({ serviceProviders: result.json.items });
        if (this.state.serviceProviders.length === 1) {
            await this.setState({ recipientPractitionerId: this.state.serviceProviders[0].id });
        }
    }

    private async assignToColleague() {
        const recipient = {
            recipientOrganizationId: this.props.request.recipientOrganizationId,
            recipientPractitionerId: this.state.recipientPractitionerId,
        };
        const { errors, item } = validate(requestEditRecipientSchema, recipient);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }
        const url = `/api/requests/${this.props.request.id}/giveProcess`;
        await fetchJson(url, 'POST', item);

        await this.props.onAssign();
    }
}
